import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getDarkMode, setDarkMode as persistDarkMode } from '../services/localPreferenceService';

/**
 * App-wide color tokens. Every screen should read colors from useTheme() rather than
 * hardcoding hex values, so toggling dark mode actually re-colors the whole app instead
 * of just the Settings screen's own switch.
 */
export interface ThemeColors {
  background: string;
  surface: string;
  card: string;
  border: string;
  divider: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textInverse: string;
  primary: string;
  primaryLight: string;
  danger: string;
  dangerLight: string;
  success: string;
  successLight: string;
  warning: string;
  warningLight: string;
  overlay: string;
  shadow: string;
  /** The web header's navy band (approved design A, 2 Nov 2026): its gradient, the line under it. */
  headerFrom: string;
  headerTo: string;
  headerAccent: string;
  /** Text on the band, and the frosted controls (search, chips, buttons) that sit on it. */
  headerText: string;
  headerTextMuted: string;
  headerControl: string;
  headerControlBorder: string;
}

// Brand primary — set explicitly to the user's chosen brand blue rather than any of the
// several ad-hoc blues (#2563EB, #1E40AF, #347FD5, etc.) individual screens used before
// this theme system existed.
const BRAND_PRIMARY = '#1B70D1';

export const lightColors: ThemeColors = {
  background: '#EEF3FA',
  surface: '#FFFFFF',
  card: '#FFFFFF',
  border: '#E2E8F0',
  divider: '#F1F5F9',
  textPrimary: '#1E293B',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  textInverse: '#FFFFFF',
  primary: BRAND_PRIMARY,
  primaryLight: '#EFF6FF',
  danger: '#EF4444',
  dangerLight: '#FEE2E2',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  overlay: 'rgba(15, 23, 42, 0.4)',
  shadow: '#000000',
  // The sidebar's navy (WebSidebar), deepening into the brand blue.
  headerFrom: '#032158',
  headerTo: '#0E3B87',
  headerAccent: BRAND_PRIMARY,
  headerText: '#FFFFFF',
  headerTextMuted: '#A9BDDF',
  headerControl: 'rgba(255, 255, 255, 0.12)',
  headerControlBorder: 'rgba(255, 255, 255, 0.28)',
};

export const darkColors: ThemeColors = {
  background: '#0F172A',
  surface: '#1E293B',
  card: '#1E293B',
  border: '#334155',
  divider: '#293548',
  textPrimary: '#F1F5F9',
  textSecondary: '#94A3B8',
  textMuted: '#64748B',
  textInverse: '#0F172A',
  primary: '#3B82F6',
  primaryLight: '#1E3A5F',
  danger: '#F87171',
  dangerLight: '#4C1D1D',
  success: '#34D399',
  successLight: '#134E3A',
  warning: '#FBBF24',
  warningLight: '#4B3B0F',
  overlay: 'rgba(0, 0, 0, 0.6)',
  shadow: '#000000',
  headerFrom: '#020F2B',
  headerTo: '#0B2D6B',
  headerAccent: '#3B82F6',
  headerText: '#FFFFFF',
  headerTextMuted: '#94A8CC',
  headerControl: 'rgba(255, 255, 255, 0.10)',
  headerControlBorder: 'rgba(255, 255, 255, 0.22)',
};

interface ThemeContextValue {
  isDark: boolean;
  colors: ThemeColors;
  /** Sets and persists dark mode. Renamed from the raw setter so call sites read intentionally. */
  toggleDarkMode: (value: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  isDark: false,
  colors: lightColors,
  toggleDarkMode: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    getDarkMode().then(setIsDark);
  }, []);

  const toggleDarkMode = (value: boolean) => {
    setIsDark(value);
    persistDarkMode(value).catch(() => {});
  };

  const colors = useMemo(() => (isDark ? darkColors : lightColors), [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, colors, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
